/**
 * RTK Query endpoints cho module Sales Control (page `frmCtrlSalesMain`).
 *
 * - Filter dùng chung header: `startDate`, `endDate`, `isFinalized` — bám
 *   `form_frmCtrlSalesMain.txt` (`start_date`, `end_date`, `txtIsForced`).
 * - Mỗi tab có 1 endpoint list riêng, URL `/sale-controls/<resource>`.
 * - Backend endpoint chưa được implement; các hook sẽ trả về list rỗng cho đến
 *   khi `backend/app/api/v1/endpoints/sale_controls.py` được thêm.
 */
import { apiSlice } from './apiSlice'

export const SALE_CONTROL_EXPORT_TOKEN = 'JDHK-NDEB-DBCK-DGEJ'

export interface SaleControlListResponse {
  items: Record<string, unknown>[]
  total: number
  page: number
  pageSize: number
}

export type SaleControlFinalizedFilter = 'all' | 'yes' | 'no'

export interface SaleControlListQuery {
  page?: number
  pageSize?: number
  q?: string
  /** ISO `YYYY-MM-DD`, optional — apply on `delivery_from` (shipment period). */
  startDate?: string
  endDate?: string
  /** ComboBox `txtIsForced` — `all` ⇒ omit. */
  isFinalized?: SaleControlFinalizedFilter
}

const trimQuery = (args: SaleControlListQuery): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  if (typeof args.page === 'number') out.page = args.page
  if (typeof args.pageSize === 'number') out.pageSize = args.pageSize
  if (args.q?.trim()) out.q = args.q.trim()
  if (args.startDate?.trim()) out.startDate = args.startDate.trim()
  if (args.endDate?.trim()) out.endDate = args.endDate.trim()
  if (args.isFinalized && args.isFinalized !== 'all') out.isFinalized = args.isFinalized
  return out
}

const saleControlApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /** Tab 1 — `public_mv_local_s` (Access query `CtrlSalesQryLocalS`). */
    getCtrlSalesLocalS: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/local-s', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 2 — Sales Allocation (`CtrlSalesQrySalesAlc`). Stub. */
    getCtrlSalesSaleAlc: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/sale-alc', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 3 — Delivery Order (`CtrlSalesQryDO`). Stub. */
    getCtrlSalesDeliveryOrder: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/delivery-order', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 4 — Invoice (`CtrlSalesQryInvoice`). Stub. */
    getCtrlSalesInvoice: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/invoice', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 5 — Receive Order (`CtrlSalesQryRO`). Stub. */
    getCtrlSalesReceiveOrder: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/receive-order', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 6 — Claim (`CtrlSalesQryClaim`). Stub. */
    getCtrlSalesClaim: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/claim', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 7 — Credit Note (`CtrlSalesQryCredit`). Stub. */
    getCtrlSalesCredit: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/credit-note', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 8 — Debit Note (`CtrlSalesQryDebit`). Stub. */
    getCtrlSalesDebit: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/debit-note', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 9 — Internal Moving (`CtrlSalesQryMoving`). Stub. */
    getCtrlSalesMoving: builder.query<SaleControlListResponse, SaleControlListQuery>({
      query: (args) => ({ url: '/sale-controls/moving', params: trimQuery(args) }),
      providesTags: ['SaleControl'],
    }),

    /** Tab 1 — toolbar `btnForcedClose` (form `frmCtrlSalesQryLocalS_CloseAdd`). */
    forcedCloseLocalS: builder.mutation<{ message: string }, { contractSale: string; finalizedDate: string; note?: string }>({
      query: (body) => ({ url: '/sale-controls/local-s/forced-close', method: 'POST', body }),
      invalidatesTags: ['SaleControl'],
    }),

    /** Tab 1 — toolbar `btnUnforced` (admin only). */
    reopenLocalS: builder.mutation<{ message: string }, { contractSale: string }>({
      query: (body) => ({ url: '/sale-controls/local-s/reopen', method: 'POST', body }),
      invalidatesTags: ['SaleControl'],
    }),

    /** Tab 1 — toolbar `btnFillingDocs` (toggle `is_ctrl_filldoc`). */
    toggleFillingDocsLocalS: builder.mutation<{ message: string }, { contractSale: string; isCtrlFilldoc: boolean }>({
      query: (body) => ({ url: '/sale-controls/local-s/filling-docs', method: 'POST', body }),
      invalidatesTags: ['SaleControl'],
    }),
  }),
})

export const {
  useGetCtrlSalesLocalSQuery,
  useGetCtrlSalesSaleAlcQuery,
  useGetCtrlSalesDeliveryOrderQuery,
  useGetCtrlSalesInvoiceQuery,
  useGetCtrlSalesReceiveOrderQuery,
  useGetCtrlSalesClaimQuery,
  useGetCtrlSalesCreditQuery,
  useGetCtrlSalesDebitQuery,
  useGetCtrlSalesMovingQuery,
  useForcedCloseLocalSMutation,
  useReopenLocalSMutation,
  useToggleFillingDocsLocalSMutation,
} = saleControlApi

export default saleControlApi
